import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiRefreshCw, FiActivity, FiSearch, FiDownload, FiX, FiArrowLeft } from 'react-icons/fi';

const AdminLogs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all');
  const [entityTypes, setEntityTypes] = useState([]);
  const [ipFilter, setIpFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userFilter, setUserFilter] = useState(null);
  const [userName, setUserName] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  const PAGE_SIZE = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: PAGE_SIZE,
        filter
      };
      if (userFilter) params.userId = userFilter;
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axios.get('/api/admin/logs', { params });
      if (response.data.success) {
        setLogs(response.data.activities);
        setTotalPages(response.data.pagination.pages);
        setTotal(response.data.pagination.total);
        setEntityTypes(response.data.filters.entityTypes);
      } else {
        toast.error('Failed to fetch activity logs');
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Error fetching activity logs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filter, userFilter, search, startDate, endDate]);

  // Debounce search input -> committed search
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, search]);

  // Fetch user name when userId query param set
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('userId');
    if (userId) {
      setUserFilter(userId);
      axios.get(`/api/admin/users/${userId}`)
        .then(r => {
          if (r.data.success) {
            setUserName(`${r.data.user.firstName} ${r.data.user.lastName}`);
          }
        })
        .catch(() => {});
    }
  }, [location.search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Client-side IP filter (post-fetch) — kept for parity with previous UX.
  const filteredLogs = ipFilter
    ? logs.filter(log => log.ipAddress && log.ipAddress.toLowerCase().includes(ipFilter.toLowerCase()))
    : logs;

  const resetFilters = () => {
    setSearchInput('');
    setSearch('');
    setFilter('all');
    setIpFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const exportCsv = async () => {
    try {
      const params = {
        page: 1,
        limit: 500,
        filter
      };
      if (userFilter) params.userId = userFilter;
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axios.get('/api/admin/logs', { params });
      const activities = response.data.activities || [];

      if (activities.length === 0) {
        toast.error('No rows to export');
        return;
      }

      const escape = (val) => {
        if (val === null || val === undefined) return '';
        const s = typeof val === 'object' ? JSON.stringify(val) : String(val);
        return `"${s.replace(/"/g, '""')}"`;
      };

      const headers = ['Timestamp', 'User', 'Email', 'Action', 'Entity Type', 'Entity ID', 'Description', 'IP Address', 'Metadata'];
      const rows = activities.map(log => [
        format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm:ss'),
        log.user?.name || 'System',
        log.user?.email || '',
        log.action || '',
        log.entityType || '',
        log.entityId || '',
        log.description || '',
        log.ipAddress || '',
        log.metadata ? JSON.stringify(log.metadata) : ''
      ]);
      const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leavewise-logs-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${activities.length} rows`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed');
    }
  };

  const actionBadge = (action) => {
    if (!action) return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    const a = action.toLowerCase();
    if (a.includes('create') || a.includes('approve') || a.includes('activate')) {
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    }
    if (a.includes('update') || a.includes('login') || a.includes('role')) {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    }
    if (a.includes('delete') || a.includes('reject')) {
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    }
    if (a.includes('suspend') || a.includes('cancel')) {
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    }
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  const entityJumpLink = (log) => {
    if (!log.entityType || !log.entityId) return null;
    const type = log.entityType.toLowerCase();
    const map = {
      user: `/admin/users?userId=${log.entityId}`,
      team: `/admin/teams?teamId=${log.entityId}`,
      leave: null,
      payment: null
    };
    return map[type] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {userFilter ? `Activity Logs for ${userName || 'User'}` : 'Activity Logs'}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">Monitor system activity and user actions</p>
          {userFilter && (
            <button
              onClick={() => {
                setUserFilter(null);
                setUserName('');
                navigate('/admin/logs');
              }}
              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              <FiArrowLeft className="w-3.5 h-3.5" />
              Back to all logs
            </button>
          )}
        </div>

        {/* Filters bar */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
            {/* Search */}
            <div className="md:col-span-2 relative">
              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search action, description, metadata…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-8 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-gray-900 dark:text-white placeholder-gray-400"
              />
            </div>

            {/* Entity type filter */}
            <select
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setCurrentPage(1); }}
              className="px-2.5 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
            >
              <option value="all">All Types</option>
              {entityTypes.map(t => (
                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
              ))}
            </select>

            {/* Date range */}
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
              className="px-2.5 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
              title="From date"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
              className="px-2.5 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white"
              title="To date"
            />

            {/* IP filter */}
            <input
              type="text"
              placeholder="Filter by IP…"
              value={ipFilter}
              onChange={(e) => setIpFilter(e.target.value)}
              className="px-2.5 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm text-gray-900 dark:text-white placeholder-gray-400"
            />
          </div>

          <div className="flex items-center justify-between mt-2">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {loading ? 'Loading…' : (
                <>
                  {total.toLocaleString()} total
                  {(search || startDate || endDate || filter !== 'all' || ipFilter) && (
                    <button
                      onClick={resetFilters}
                      className="ml-2 text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      clear filters
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={exportCsv}
                disabled={loading || total === 0}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                <FiDownload className="w-3.5 h-3.5" />
                Export CSV
              </button>
              <button
                onClick={fetchLogs}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                <FiRefreshCw className="w-3.5 h-3.5" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Logs table */}
        {loading ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-8 flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Loading activity logs…</span>
          </div>
        ) : filteredLogs.length > 0 ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">When</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Action</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Entity</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Description</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                  {filteredLogs.map((log) => (
                    <tr
                      key={log.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer"
                      onClick={() => setSelectedLog(log)}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
                        {format(new Date(log.createdAt), 'MMM d, HH:mm:ss')}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs font-medium text-gray-900 dark:text-white">
                          {log.user ? log.user.name : 'System'}
                        </div>
                        {log.user && (
                          <div className="text-[10px] text-gray-500 dark:text-gray-400">{log.user.email}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${actionBadge(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400 capitalize">
                        {log.entityType || '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 max-w-md truncate">
                        {log.description}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {log.ipAddress || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-8 text-center">
            <FiActivity className="h-10 w-10 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No activity logs match your filters</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 px-1">
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
              {' '}· Showing {filteredLogs.length} of {total.toLocaleString()}
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1 text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-2.5 py-1 text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
              >
                Prev
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1 text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1 text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-700 dark:text-gray-300"
              >
                Last
              </button>
            </div>
          </div>
        )}

        {/* Log detail modal */}
        {selectedLog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Activity detail</h3>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-3 overflow-y-auto">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-0.5">When</label>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {format(new Date(selectedLog.createdAt), 'MMM d, yyyy HH:mm:ss')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-0.5">Action</label>
                    <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded ${actionBadge(selectedLog.action)}`}>
                      {selectedLog.action}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-0.5">User</label>
                  {selectedLog.user ? (
                    <div>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedLog.user.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{selectedLog.user.email} · {selectedLog.user.role}</p>
                      <Link
                        to={`/admin/logs?userId=${selectedLog.user.id}`}
                        onClick={() => setSelectedLog(null)}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        View all this user's activity →
                      </Link>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">System</p>
                  )}
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-0.5">Description</label>
                  <p className="text-sm text-gray-900 dark:text-white">{selectedLog.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-0.5">Entity Type</label>
                    <p className="text-sm text-gray-900 dark:text-white capitalize">{selectedLog.entityType || '—'}</p>
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-0.5">Entity ID</label>
                    <p className="text-xs font-mono text-gray-900 dark:text-white break-all">{selectedLog.entityId || '—'}</p>
                    {entityJumpLink(selectedLog) && (
                      <Link
                        to={entityJumpLink(selectedLog)}
                        onClick={() => setSelectedLog(null)}
                        className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Open this {selectedLog.entityType} →
                      </Link>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-0.5">IP Address</label>
                  <p className="text-xs font-mono text-gray-900 dark:text-white">{selectedLog.ipAddress || '—'}</p>
                </div>

                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div>
                    <label className="block text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-0.5">Metadata</label>
                    <pre className="text-[11px] text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 p-2.5 rounded-md overflow-x-auto border border-gray-200 dark:border-gray-700">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setSelectedLog(null)}
                  className="px-3 py-1.5 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLogs;
